import React from 'react';
import { useTranslation } from 'react-i18next';

interface Project {
    title: string;
    description: string;
    tech: string[];
    image: string;
    link: string;
}

const ProjectCard = ({ project, className = "" }: { project: Project; className?: string }) => {
    const { t } = useTranslation();
    return (
        <div
            className={`flex-none w-[300px] md:w-[400px] bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${className}`}
        >
            <div className="h-48 overflow-hidden bg-slate-100 relative">
                <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Imagen+del+Proyecto';
                    }}
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300" />
            </div>

            <div className="p-6 flex flex-col h-[280px]">
                <h3 className="text-xl font-bold text-slate-800 mb-2">{project.title}</h3>

                <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech) => (
                        <span
                            key={tech}
                            className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full"
                        >
                            {tech}
                        </span>
                    ))}
                </div>

                <p className="text-slate-600 text-sm mb-6 flex-grow line-clamp-4">
                    {project.description}
                </p>

                <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
                >
                    {t('projects.view_project')}
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
            </div>
        </div>
    );
};

export const ProjectsSection = () => {
    const { t } = useTranslation();

    const projects: Project[] = [
        {
            title: t('projects.items.couchsurfing.title'),
            description: t('projects.items.couchsurfing.desc'),
            tech: ["Flutter", "Dart"],
            image: "/images/projects/pic_couchsurfing.png",
            link: "https://play.google.com/store/apps/details?id=com.couchsurfing.mobile.android&hl=es_MX"
        },
        {
            title: t('projects.items.cashmind.title'),
            description: t('projects.items.cashmind.desc'),
            tech: ["Flutter", "Dart"],
            image: "/images/projects/pic_cashmind.png",
            link: "https://play.google.com/store/apps/details?id=com.cashmindwallet.app&hl=es_MX"
        },
        {
            title: t('projects.items.nubank.title'),
            description: t('projects.items.nubank.desc'),
            tech: ["Flutter", "Android", "iOS"],
            image: "/images/projects/pic_nubank.png",
            link: "https://play.google.com/store/search?q=Nubank&c=apps"
        },
        {
            title: t('projects.items.crabi.title'),
            description: t('projects.items.crabi.desc'),
            tech: ["Flutter", "Android", "Stripe"],
            image: "/images/projects/pic_crabi.png",
            link: "https://play.google.com/store/apps/details?id=com.crabi.customerjourneys"
        }
    ];

    return (
        <section id="projects" className="py-20 bg-slate-50 overflow-hidden">
            <div className="container mx-auto px-4 mb-12">
                <h2 className="text-3xl font-bold text-center text-slate-800 mb-4">{t('projects.title')}</h2>
                <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>

            {/* Desktop View: Static Grid, No Animation, No Duplicates */}
            <div className="hidden lg:flex justify-center flex-wrap gap-8 container mx-auto px-4">
                {projects.map((project, index) => (
                    <ProjectCard key={`desktop-project-${index}`} project={project} />
                ))}
            </div>

            {/* Mobile/Tablet View: manual swipe with snap, no auto-scroll */}
            <div className="lg:hidden relative w-full">
                <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide py-4 px-4 gap-6">
                    {projects.map((project, index) => (
                        <ProjectCard key={`mobile-project-${index}`} project={project} className="snap-center" />
                    ))}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
            .scrollbar-hide::-webkit-scrollbar {
                display: none;
            }
            .scrollbar-hide {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }
        `}} />
        </section>
    );
};

